import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Modal, ModalHeader, ModalBody, ModalFooter} from 'reactstrap';
import {useTranslation} from 'react-i18next';
import {Spinner} from '../../Spinner';
import {SpinnerButton} from '../../SpinnerButton';
import {MediaPlanApi} from '../../../api/mediaPlanApi';
import {CellPeek} from './CellPeek';
import {tabUrl} from './sheetUrl';

const NS = 'media_plan.interpretation';
const cellKey = (value) => String(value || '').trim().toUpperCase();
const validCell = (value) => /^[A-Z]+[1-9]\d*$/.test(cellKey(value));
const nameKey = (value) => String(value || '').normalize('NFC').trim().replace(/\s+/g, ' ').toLocaleLowerCase();
const campaignNameKey = (campaign) => `${campaign.platform || ''}::${nameKey(campaign.name)}`;
const duplicateKeys = (keys) => {
    const seen = new Set();
    const duplicates = new Set();
    for (const key of keys) {
        if (seen.has(key)) duplicates.add(key);
        else seen.add(key);
    }
    return duplicates;
};
const originalValue = (record, originalKey, currentKey) =>
    Object.prototype.hasOwnProperty.call(record, originalKey) ? record[originalKey] : record[currentKey];
const rowCellOptional = (record) => {
    const amount = originalValue(record, 'source_amount', 'amount');
    return amount == null || (Number.isFinite(Number(amount)) && Number(amount) <= 0);
};
const validRowCell = (value, original) => validCell(value) || (!cellKey(value) && rowCellOptional(original));

// La lectura original permanece en `source`; el formulario sólo guarda decisiones de nombres y
// celdas. Nunca acepta montos manuales ni modifica el snapshot o los presupuestos ya aplicados.
export function InterpretationReview({proposalId, clientId, externalId, onSaved, onClose,
                                      onCurrentProposal, onReloadProposal}) {
    const {t, i18n} = useTranslation();
    const [source, setSource] = useState(null);
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [stale, setStale] = useState(false);
    const [reloadParent, setReloadParent] = useState(false);
    const requestNumber = useRef(0);
    const lookupProposalId = useRef(proposalId);
    const currentProposalCallback = useRef(onCurrentProposal);
    currentProposalCallback.current = onCurrentProposal;

    const showError = useCallback((response, fromLoad = false) => {
        const reason = response && (response.reason_code || (response.raw_body || {}).reason_code);
        const isStale = reason === 'STALE_INTERPRETATION' || (response && response.http_status === 409);
        if (isStale) setStale(true);
        const raw = (response && response.raw_body) || {};
        const currentId = response && (raw.current_proposal_id || raw.concurrent_proposal_id || response.current_proposal_id);
        if (isStale && currentId) lookupProposalId.current = currentId;
        setReloadParent(!!(fromLoad && isStale && !currentId));
        const message = reason === 'INVALID_INTERPRETATION'
            && ((response.raw_body || {}).error_message || response.error_message);
        setError(isStale ? t(`${NS}.stale`) : message || t(`${NS}.error`));
    }, [t]);

    const load = useCallback(() => {
        const number = ++requestNumber.current;
        setLoading(true);
        setSaving(false);
        setError('');
        MediaPlanApi.getInterpretation(lookupProposalId.current, clientId)
            .then((response) => {
                if (number !== requestNumber.current) return;
                const reading = response && response.interpretation;
                if (response && response.status === 'OK' && reading && Array.isArray(reading.sections)) {
                    setSource(reading);
                    lookupProposalId.current = reading.proposal_id || lookupProposalId.current;
                    setSections(reading.sections.map((section) => ({...section,
                        campaigns: (section.campaigns || []).map((campaign) => ({...campaign})),
                        allocations: (section.allocations || []).map((allocation) => ({...allocation})),
                    })));
                    setStale(false);
                    setReloadParent(false);
                    if (response.proposal && currentProposalCallback.current) currentProposalCallback.current(response.proposal);
                } else showError(response, true);
            })
            .catch(() => number === requestNumber.current && showError(null))
            .finally(() => number === requestNumber.current && setLoading(false));
    }, [clientId, showError]);

    useEffect(() => {
        load();
        return () => { requestNumber.current += 1; };
    }, [load]);

    const duplicateNames = duplicateKeys(sections.map((section) => nameKey(section.name)));
    const sectionChecks = sections.map((section, index) => {
        const original = source.sections[index];
        const duplicateCampaigns = duplicateKeys(section.campaigns.map(campaignNameKey));
        return {duplicateCampaigns,
            campaignsValid: duplicateCampaigns.size === 0 && section.campaigns.every((campaign, rowIndex) =>
                nameKey(campaign.name) && validRowCell(campaign.cell, original.campaigns[rowIndex])),
            allocationsValid: section.allocations.every((allocation, rowIndex) =>
                validRowCell(allocation.cell, original.allocations[rowIndex])),
        };
    });
    const fieldsValid = !!source && !!source.fingerprint && sections.length > 0
        && sections.every((section, index) => nameKey(section.name) && !duplicateNames.has(nameKey(section.name))
            && (!cellKey(section.total_cell) || validCell(section.total_cell))
            && sectionChecks[index].campaignsValid && sectionChecks[index].allocationsValid);
    const pendingConfirmations = sections.filter((section) => section.confirmed_platform_spend_and_scope !== true).length;
    const canSave = fieldsValid && !stale && pendingConfirmations === 0;

    const updateSection = (key, patch, isConfirmation = false) => setSections((current) => current.map((section) =>
        section.key === key ? {...section, ...patch,
            ...(!isConfirmation ? {confirmed_platform_spend_and_scope: false} : {})} : section));
    const updateCampaign = (sectionKey, campaignKey, patch) => setSections((current) => current.map((section) =>
        section.key === sectionKey ? {...section, confirmed_platform_spend_and_scope: false,
            campaigns: section.campaigns.map((campaign) => campaign.key === campaignKey
                ? {...campaign, ...patch} : campaign)} : section));
    const updateAllocation = (sectionKey, allocationKey, value) => setSections((current) => current.map((section) =>
        section.key === sectionKey ? {...section, confirmed_platform_spend_and_scope: false,
            allocations: section.allocations.map((allocation) => allocation.key === allocationKey
                ? {...allocation, cell: value} : allocation)} : section));

    const save = (reset = false) => {
        if (!source || loading || saving || stale || (!reset && !canSave)) return;
        const number = ++requestNumber.current;
        setSaving(true);
        setError('');
        const decision = {fingerprint: source.fingerprint, expected_revision_id: source.revision_id ?? null};
        if (reset) decision.action = 'RESET';
        else decision.sections = sections.map((section) => ({
            key: section.key, name: section.name.trim(), total_cell: cellKey(section.total_cell),
            confirmed_platform_spend_and_scope: true,
            campaigns: section.campaigns.map((campaign) => ({
                key: campaign.key, name: campaign.name.trim(), cell: cellKey(campaign.cell),
            })),
            allocations: section.allocations.map((allocation) => ({key: allocation.key, cell: cellKey(allocation.cell)})),
        }));
        MediaPlanApi.saveInterpretation(source.proposal_id || proposalId, clientId, decision)
            .then((response) => {
                if (number !== requestNumber.current) return;
                if (response && response.status === 'OK' && response.proposal && response.interpretation) {
                    onSaved(response);
                } else showError(response);
            })
            .catch(() => number === requestNumber.current && showError(null))
            .finally(() => number === requestNumber.current && setSaving(false));
    };

    const amountLabel = (amount) => {
        if (amount === null || amount === undefined || !Number.isFinite(Number(amount))) return t(`${NS}.no_amount`);
        const formatted = new Intl.NumberFormat(i18n.language === 'en' ? 'en-US' : 'es-AR',
            {maximumFractionDigits: 2}).format(Number(amount));
        return `${source.currency || ''} ${formatted}`.trim();
    };
    const sourceCell = (cell, amount) => {
        const baseUrl = source && externalId ? tabUrl(externalId, source.tab_gid) : null;
        const url = baseUrl && cell ? baseUrl + `${baseUrl.includes('#') ? '&' : '#'}range=${encodeURIComponent(cell)}` : baseUrl;
        return (
            <span className="mp-reading-source-amount">
                <span>{t(`${NS}.original_amount`)} {amountLabel(amount)}</span>
                {cell && url ? (
                    <CellPeek agencyClientId={clientId} snapshotId={source.snapshot_id} cell={cell}>
                        <a href={url} target="_blank" rel="noopener noreferrer">{cell}</a>
                    </CellPeek>
                ) : <span>{cell || t(`${NS}.no_cell`)}</span>}
            </span>
        );
    };
    const cellInput = (id, value, onChange, optional = false, verified = {}) => (
        <div className="mp-reading-field">
            <label htmlFor={id}>{t(`${NS}.budget_cell`)}</label>
            <input id={id} type="text" className="form-control" value={value || ''}
                   disabled={saving} spellCheck={false} autoComplete="off"
                   aria-invalid={!(optional && !cellKey(value)) && !validCell(value)} aria-describedby={`${id}-help`}
                   onChange={(event) => onChange(event.target.value)}/>
            <span id={`${id}-help`} className="mp-reading-help">{t(`${NS}.cell_help`)}</span>
            {validCell(value) && <span className="mp-reading-help" aria-live="polite">
                {cellKey(value) === cellKey(verified.cell) && verified.amount != null
                    && Number.isFinite(Number(verified.amount))
                    ? `${t(`${NS}.selected_amount`)} ${amountLabel(verified.amount)}`
                    : t(`${NS}.amount_pending`)}
            </span>}
            {optional && <span className="mp-reading-help">{t(`${NS}.${optional === 'row' ? 'optional_row' : 'optional_total'}`)}</span>}
            {validCell(value) && source && externalId && (
                <CellPeek agencyClientId={clientId} snapshotId={source.snapshot_id} cell={cellKey(value)}>
                    <a href={`${tabUrl(externalId, source.tab_gid)}${source.tab_gid == null ? '#' : '&'}range=${encodeURIComponent(cellKey(value))}`}
                       target="_blank" rel="noopener noreferrer">{t(`${NS}.view_cell`)}</a>
                </CellPeek>
            )}
            {!(optional && !cellKey(value)) && !validCell(value) && <span className="mp-reading-invalid">{t(`${NS}.invalid_cell`)}</span>}
        </div>
    );

    return (
        <Modal isOpen toggle={saving ? undefined : onClose} size="xl"
               className="mp-interpretation-review" labelledBy="mp-reading-title">
            <ModalHeader toggle={saving ? undefined : onClose}>
                <span id="mp-reading-title">{t(`${NS}.title`)}</span>
            </ModalHeader>
            <form onSubmit={(event) => { event.preventDefault(); save(); }}>
                <ModalBody>
                    <p>{t(`${NS}.intro`)}</p>
                    <p className="mp-reading-scope">{t(`${NS}.scope`)}</p>
                    {error && <div className="mp-reading-error" role="alert">
                        <span>{error}</span>
                        {(stale || !source) && <button type="button" className="btn -negative"
                            disabled={loading || saving} onClick={() => {
                                if (reloadParent && onReloadProposal) onReloadProposal();
                                else load();
                            }}>{t(`${NS}.reload`)}</button>}
                    </div>}
                    {loading ? <div className="box-loader"><Spinner color="positive"/></div> : source && (
                        <>
                            <p className="mp-reading-tab">{t(`${NS}.tab`)} <strong>{source.tab_title}</strong></p>
                            {(source.warnings || []).some((warning) => warning && warning.note) && <aside>
                                <h3>{t(`${NS}.original_warnings`)}</h3>
                                {(source.warnings || []).filter((warning) => warning && warning.note).map((warning, index) => (
                                    <p key={index} className="mp-reading-warning">{warning.note}</p>
                                ))}
                            </aside>}
                            {sections.length === 0 && <p>{t(`${NS}.empty`)}</p>}
                            {sections.map((section, index) => {
                                const original = source.sections[index];
                                const checks = sectionChecks[index];
                                const nameInvalid = !nameKey(section.name) || duplicateNames.has(nameKey(section.name));
                                return (
                                    <section key={section.key} className="mp-reading-section"
                                             aria-labelledby={`mp-reading-block-${section.key}`}>
                                        <h3 id={`mp-reading-block-${section.key}`}>
                                            {t(`${NS}.block`, {count: index + 1})}
                                        </h3>
                                        <div className="mp-reading-source">
                                            <span>{t(`${NS}.source_name`)} <strong>{original.source_name || t(`${NS}.unnamed`)}</strong></span>
                                            {sourceCell(originalValue(original, 'source_total_cell', 'total_cell'),
                                                originalValue(original, 'source_total_amount', 'total_amount'))}
                                        </div>
                                        <div className="mp-reading-fields">
                                            <div className="mp-reading-field">
                                                <label htmlFor={`mp-reading-name-${section.key}`}>{t(`${NS}.block_name`)}</label>
                                                <input id={`mp-reading-name-${section.key}`} type="text" className="form-control"
                                                       value={section.name || ''} disabled={saving} required maxLength={100} aria-invalid={nameInvalid}
                                                       onChange={(event) => updateSection(section.key, {name: event.target.value})}/>
                                                {nameInvalid && <span className="mp-reading-invalid">{t(`${NS}.unique_name`)}</span>}
                                            </div>
                                            {cellInput(`mp-reading-total-${section.key}`, section.total_cell,
                                                (value) => updateSection(section.key, {total_cell: value}), true,
                                                {cell: original.total_cell, amount: original.total_amount})}
                                        </div>
                                        {section.allocations.length > 0 && <div className="mp-reading-allocations">
                                            <h4>{t(`${NS}.allocations`)}</h4>
                                            {section.allocations.map((allocation, allocationIndex) => {
                                                const raw = original.allocations[allocationIndex];
                                                const platform = {GOOGLE: 'Google Ads', META: 'Meta Ads', FACEBOOK: 'Meta Ads',
                                                    TIKTOK: 'TikTok Ads', LINKEDIN: 'LinkedIn Ads'}[allocation.platform] || allocation.platform;
                                                return <div key={allocation.key} className="mp-reading-campaign">
                                                    <strong>{platform}</strong>
                                                    {sourceCell(originalValue(raw, 'source_cell', 'cell'), originalValue(raw, 'source_amount', 'amount'))}
                                                    {cellInput(`mp-reading-allocation-${section.key}-${allocation.key}`, allocation.cell,
                                                        (value) => updateAllocation(section.key, allocation.key, value),
                                                        rowCellOptional(raw) ? 'row' : false, raw)}
                                                </div>;
                                            })}
                                        </div>}
                                        <details className="mp-reading-campaigns">
                                            <summary>{t(`${NS}.campaigns`, {count: section.campaigns.length})}
                                                {!checks.campaignsValid && <span className="mp-reading-invalid">{t(`${NS}.campaigns_invalid`)}</span>}
                                            </summary>
                                            {section.campaigns.map((campaign, campaignIndex) => {
                                                const raw = original.campaigns[campaignIndex];
                                                const rowKey = `${section.key}-${campaign.key}`;
                                                const nameInvalid = !nameKey(campaign.name)
                                                    || checks.duplicateCampaigns.has(campaignNameKey(campaign));
                                                return (
                                                    <div key={campaign.key} className="mp-reading-campaign">
                                                        <div className="mp-reading-source">
                                                            <span>{t(`${NS}.source_name`)} <strong>{raw.source_name || t(`${NS}.unnamed`)}</strong>
                                                                {raw.platform_raw && <span className="mp-reading-platform"> · {raw.platform_raw}</span>}
                                                            </span>
                                                            {sourceCell(originalValue(raw, 'source_cell', 'cell'),
                                                                originalValue(raw, 'source_amount', 'amount'))}
                                                        </div>
                                                        <div className="mp-reading-fields">
                                                            <div className="mp-reading-field">
                                                                <label htmlFor={`mp-reading-campaign-${rowKey}`}>{t(`${NS}.campaign_name`)}</label>
                                                                <input id={`mp-reading-campaign-${rowKey}`} type="text" className="form-control"
                                                                       value={campaign.name || ''} required disabled={saving} maxLength={500}
                                                                       aria-invalid={nameInvalid}
                                                                       onChange={(event) => updateCampaign(section.key, campaign.key,
                                                                           {name: event.target.value})}/>
                                                                {nameInvalid && <span className="mp-reading-invalid">{t(`${NS}.unique_campaign`)}</span>}
                                                            </div>
                                                            {cellInput(`mp-reading-cell-${rowKey}`, campaign.cell,
                                                                (value) => updateCampaign(section.key, campaign.key, {cell: value}),
                                                                rowCellOptional(raw) ? 'row' : false, raw)}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </details>
                                        <div className="form-check mp-reading-confirm">
                                            <input type="checkbox" className="form-check-input" id={`mp-reading-confirm-${section.key}`}
                                                   disabled={saving} checked={section.confirmed_platform_spend_and_scope === true}
                                                   onChange={(event) => updateSection(section.key,
                                                       {confirmed_platform_spend_and_scope: event.target.checked}, true)}/>
                                            <label className="form-check-label" htmlFor={`mp-reading-confirm-${section.key}`}>
                                                {t(`${NS}.confirm_scope`)}
                                            </label>
                                        </div>
                                    </section>
                                );
                            })}
                        </>
                    )}
                </ModalBody>
                <ModalFooter>
                    {source && !loading && !stale && sections.length > 0 && !canSave && (
                        <p id="mp-reading-save-help" className="mp-reading-help w-100 mb-0" role="status">
                            {!fieldsValid ? t(`${NS}.fix_fields`)
                                : t(`${NS}.pending_confirmations`, {count: pendingConfirmations})}
                        </p>
                    )}
                    {source && source.revision_id != null && <button type="button" className="btn -negative"
                        disabled={saving || loading || stale} onClick={() => save(true)}>{t(`${NS}.reset`)}</button>}
                    <button type="button" className="btn -negative" disabled={saving} onClick={onClose}>{t(`${NS}.cancel`)}</button>
                    {saving ? <SpinnerButton/> : <button type="submit" className="btn -alt" disabled={loading || !canSave}
                        aria-describedby={source && !loading && !stale && !canSave && sections.length > 0 ? 'mp-reading-save-help' : undefined}>
                        {t(`${NS}.save`)}
                    </button>}
                </ModalFooter>
            </form>
        </Modal>
    );
}
